/**
 * The top progress bar's timing, the way GitHub's behaves: a page change or
 * a save starts it, it creeps forward while requests are in flight, and it
 * runs to the end and fades once they settle. It stays invisible for loads
 * that finish quickly, so a fast page never flashes a bar.
 *
 * Only a navigation or a change (POST, PUT, PATCH, DELETE) starts a run.
 * Plain reads that nobody asked for — the deployment poll, the toast feed —
 * never do; they only extend a run that is already going.
 */

export interface ProgressState {
  /** 0–100: how far along the bar is drawn. */
  value: number;
  visible: boolean;
}

const SHOW_DELAY = 150;
// How long requests must stay quiet before the run is over; a page's reads
// often chain (the project, then its deployments), and the bar should not
// finish between two links of the chain.
const QUIET_AFTER = 250;
const TRICKLE_EVERY = 200;
const FADE_AFTER = 200;
const GIVE_UP_AFTER = 30_000;
// A read still waiting after this long stops holding the bar: a page that is
// quick to load does not wait for a slow background read (a live server
// inspection, a stuck request), which carries on behind the finished bar.
// Changes (saves) are not detached: the bar stays until they answer.
const SLOW_READ_AFTER = 4000;

function trickleStep(value: number): number {
  if (value < 30) return 5;
  if (value < 60) return 3;
  if (value < 80) return 1.5;
  if (value < 92) return 0.4;
  return 0;
}

export class ProgressController {
  private pending = 0;
  // Bumped when a run ends, so a request of an earlier run that answers late
  // cannot take away from the count of the run after it.
  private run = 0;
  private active = false;
  private shown = false;
  // The show delay has passed; the bar then appears as soon as a request is
  // actually waiting, never merely because a run is open.
  private delayPassed = false;
  private value = 0;
  private quietTimer: ReturnType<typeof setTimeout> | null = null;
  private showTimer: ReturnType<typeof setTimeout> | null = null;
  private fadeTimer: ReturnType<typeof setTimeout> | null = null;
  private giveUpTimer: ReturnType<typeof setTimeout> | null = null;
  private trickleTimer: ReturnType<typeof setInterval> | null = null;
  private listener: ((state: ProgressState) => void) | null = null;

  /** Tells one listener (the bar) about every change; returns the way to stop. */
  subscribe(listener: (state: ProgressState) => void): () => void {
    this.listener = listener;
    return () => {
      if (this.listener === listener) this.listener = null;
    };
  }

  /** A run begins now (a page change); more calls during a run keep it going. */
  start(): void {
    if (this.fadeTimer) {
      clearTimeout(this.fadeTimer);
      this.fadeTimer = null;
    }
    if (this.active) {
      // A page change while a run is still open (its quiet tail, or a slow
      // read) is a new load: unless the bar is already up, it gets its own
      // delay, so a fast page that follows another never flashes the bar.
      if (!this.shown) this.armShowTimer();
      this.scheduleQuiet();
      return;
    }
    this.active = true;
    this.shown = false;
    this.value = 0;
    this.emit(false);
    this.armShowTimer();
    this.trickleTimer = setInterval(() => {
      this.value = Math.min(92, this.value + trickleStep(this.value));
      if (this.shown) this.emit(true);
    }, TRICKLE_EVERY);
    this.giveUpTimer = setTimeout(() => this.finish(), GIVE_UP_AFTER);
    this.scheduleQuiet();
  }

  private armShowTimer(): void {
    if (this.showTimer) clearTimeout(this.showTimer);
    this.delayPassed = false;
    this.showTimer = setTimeout(() => {
      this.showTimer = null;
      this.delayPassed = true;
      this.showIfWaiting();
    }, SHOW_DELAY);
  }

  /** A request began; the returned function reports that it ended. */
  requestStarted(mutating: boolean): () => void {
    if (!this.active) {
      if (!mutating) return () => {};
      this.start();
    }
    this.pending += 1;
    this.clearQuiet();
    this.showIfWaiting();
    const run = this.run;
    let released = false;
    let slowTimer: ReturnType<typeof setTimeout> | null = null;
    const release = () => {
      if (released) return;
      released = true;
      if (slowTimer) clearTimeout(slowTimer);
      if (run !== this.run) return;
      this.pending = Math.max(0, this.pending - 1);
      if (this.active && this.pending === 0) this.scheduleQuiet();
    };
    if (!mutating) slowTimer = setTimeout(release, SLOW_READ_AFTER);
    return release;
  }

  private showIfWaiting(): void {
    if (this.shown || !this.delayPassed || this.pending === 0) return;
    this.shown = true;
    this.emit(true);
  }

  private scheduleQuiet(): void {
    this.clearQuiet();
    if (this.pending > 0) return;
    this.quietTimer = setTimeout(() => this.finish(), QUIET_AFTER);
  }

  private clearQuiet(): void {
    if (this.quietTimer) clearTimeout(this.quietTimer);
    this.quietTimer = null;
  }

  private finish(): void {
    this.clearQuiet();
    for (const timer of [this.showTimer, this.giveUpTimer]) if (timer) clearTimeout(timer);
    if (this.trickleTimer) clearInterval(this.trickleTimer);
    this.showTimer = this.giveUpTimer = this.trickleTimer = null;
    this.active = false;
    this.pending = 0;
    this.run += 1;
    if (!this.shown) {
      this.value = 0;
      this.emit(false);
      return;
    }
    // Run to the end, then fade; the bar goes back to zero once it is gone.
    this.value = 100;
    this.emit(true);
    this.fadeTimer = setTimeout(() => {
      this.fadeTimer = null;
      this.shown = false;
      this.emit(false);
    }, FADE_AFTER);
  }

  private emit(visible: boolean): void {
    this.listener?.({ value: this.value, visible });
  }
}

/** The one bar of the app: api.ts reports requests, the bar reports page changes. */
export const progress = new ProgressController();
